import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, tap } from 'rxjs';

import { ApiEvent } from '@app/diagnostics/models/diagnostic-event';
import {
  ApiPayloadConfig,
  DiagnosticEventType,
} from '@app/diagnostics/models/diagnostic-profile';
import { DiagnosticBufferService } from '@app/diagnostics/services/diagnostic-buffer.service';
import { DiagnosticDeepDebugConfigService } from '@app/diagnostics/services/diagnostic-deep-debug-config.service';
import { TraceContextService } from '@app/diagnostics/services/trace-context.service';

@Injectable()
export class TraceInterceptor implements HttpInterceptor {
  constructor(
    private readonly traceContext: TraceContextService,
    private readonly buffer: DiagnosticBufferService,
    private readonly deepDebugConfig: DiagnosticDeepDebugConfigService
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // Skip cross-origin requests: the traceparent header is not
    // CORS-safelisted and would trigger preflight + potential leakage of
    // the session prefix to third parties. The same-origin invariant is
    // not enforced at the application level, so guard here.
    if (!isSameOrigin(request.url)) {
      return next.handle(request);
    }

    const { traceparent, traceId } = this.traceContext.nextTraceparent();

    // Intentional override: this interceptor is the single source of truth
    // for FE→BE trace correlation. Any traceparent set upstream is replaced.
    const tracedRequest = request.clone({
      setHeaders: { traceparent },
    });
    const startedAt = performance.now();

    return next.handle(tracedRequest).pipe(
      tap({
        next: (event) => {
          if (event instanceof HttpResponse) {
            this.pushApiEvent(
              tracedRequest,
              event.status,
              performance.now() - startedAt,
              traceId,
              event.body,
              event.headers
            );
          }
        },
        error: (err: unknown) => {
          // status=0 fallback: non-HttpErrorResponse failures (e.g. a
          // downstream interceptor throwing a plain Error). HttpClient's
          // own network failures already arrive as HttpErrorResponse with
          // status=0; this branch is the defensive belt-and-braces.
          const status = err instanceof HttpErrorResponse ? err.status : 0;
          const responseBody =
            err instanceof HttpErrorResponse ? err.error : undefined;
          const responseHeaders =
            err instanceof HttpErrorResponse ? err.headers : undefined;
          this.pushApiEvent(
            tracedRequest,
            status,
            performance.now() - startedAt,
            traceId,
            responseBody,
            responseHeaders
          );
        },
      })
    );
  }

  private pushApiEvent(
    request: HttpRequest<unknown>,
    status: number,
    durationMs: number,
    traceId: string,
    responseBody: unknown,
    responseHeaders: HttpHeaders | undefined
  ): void {
    const apiPayload = this.deepDebugConfig.resolveApiPayload(
      this.buffer.getActiveProfile()
    );
    const payload: ApiEvent['payload'] = {
      method: request.method,
      url: request.url,
      status,
      durationMs,
      traceId,
    };
    if (apiPayload.requestBodyMaxChars !== undefined) {
      const captured = captureBody(
        request.body,
        apiPayload.requestBodyMaxChars
      );
      if (captured !== undefined) {
        payload.requestBody = captured;
      }
    }
    if (apiPayload.responseBodyMaxChars !== undefined) {
      const captured = captureBody(
        responseBody,
        apiPayload.responseBodyMaxChars
      );
      if (captured !== undefined) {
        payload.responseBody = captured;
      }
    }
    if (apiPayload.captureHeaders) {
      payload.requestHeaders = headersToRecord(request.headers);
      if (responseHeaders) {
        payload.responseHeaders = headersToRecord(responseHeaders);
      }
    }
    this.buffer.push({
      type: DiagnosticEventType.API,
      timestamp: Date.now(),
      // Placeholder for type satisfaction; DiagnosticBufferService.push
      // overrides this with the active profile name.
      profile: 'default',
      payload,
    });
  }
}

const TRUNCATION_SUFFIX = '…(truncated)';

/**
 * Serialize a request/response body to a sanitizer-friendly string,
 * truncating to the configured cap. Returns `undefined` when there
 * is genuinely nothing to capture (null/undefined body or zero cap).
 *
 * Non-JSON-serializable bodies (Blob, FormData, ArrayBuffer) get an
 * informative marker rather than the misleading `'{}'` that
 * `JSON.stringify` returns for a Blob with no enumerable properties.
 * The marker tells the support engineer the body had a real binary
 * payload that did not round-trip through JSON capture.
 */
function captureBody(body: unknown, maxChars: number): string | undefined {
  if (body == null || maxChars <= 0) {
    return undefined;
  }
  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    return `[Blob ${body.size}B${body.type ? ` ${body.type}` : ''}]`;
  }
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    return '[FormData]';
  }
  if (body instanceof ArrayBuffer) {
    return `[ArrayBuffer ${body.byteLength}B]`;
  }
  if (ArrayBuffer.isView(body)) {
    return `[${body.constructor.name} ${body.byteLength}B]`;
  }
  let raw: string;
  try {
    raw = typeof body === 'string' ? body : JSON.stringify(body);
  } catch {
    return '[unserializable]';
  }
  if (raw === undefined) {
    return undefined;
  }
  if (raw.length > maxChars) {
    return raw.slice(0, maxChars) + TRUNCATION_SUFFIX;
  }
  return raw;
}

function headersToRecord(headers: HttpHeaders): Record<string, string> {
  const record: Record<string, string> = {};
  for (const key of headers.keys()) {
    const value = headers.get(key);
    if (value !== null) {
      record[key] = value;
    }
  }
  return record;
}

// Exported for spec coverage. Not used outside this file otherwise.
export const __test = { captureBody, headersToRecord };

function isSameOrigin(url: string): boolean {
  // Relative URLs are always same-origin.
  if (url.startsWith('/') && !url.startsWith('//')) {
    return true;
  }
  try {
    return (
      new URL(url, window.location.origin).origin === window.location.origin
    );
  } catch {
    return false;
  }
}
